import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import NavBar from "../NavBar/NavBar";
import starimg from "../images/dancingstars/starhero.jpg";
import PressPraiseSection from "./subPages/PressPraiseSection";
import DancingWithStarsAbout from "./subPages/DancingWithStarsAbout";
import DancingWithStarsTestimonials from "./subPages/DancingWithStarsTestimonial";
import SEO from "../app/SEO";
import dancer from "../images/dancingstars/dance.jpg";

const STAR_COUNT = 160;

function StarsBurst() {
  const stars = Array.from({ length: STAR_COUNT });

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
        zIndex: 3,
      }}
    >
      {stars.map((_, i) => {
        const angle = Math.random() * Math.PI * 2;
        const distance = 180 + Math.random() * 420;
        const x = Math.cos(angle) * distance;
        const y = Math.sin(angle) * distance;

        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: 0, y: 0, scale: 0.4 }}
            animate={{
              opacity: [0, 1, 1, 0],
              x,
              y,
              scale: [0.3, 1, 0.8],
            }}
            transition={{
              duration: 6.5, // ⬅ longer flight
              times: [0, 0.25, 0.75, 1],
              delay: i * 0.06, // ⬅ gentler stagger
              ease: "easeOut",
            }}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              width: 5,
              height: 5,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, #ffffff 0%, rgba(255,255,255,0.25) 65%, transparent 100%)",
              filter: "blur(0.4px)",
            }}
          />
        );
      })}
    </Box>
  );
}

export default function DancingWithStarsPage() {
  const { t, i18n } = useTranslation("dancingstarsmain");
  const [showStars, setShowStars] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowStars(false), 16000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <SEO
        title="Dancing With The Stars Experience | Exclusive Celebrity Encounters"
        description="Discover Dancing With The Stars, an exclusive Helvetia experience where select fans share private dinners, personal conversations, and unforgettable moments with iconic celebrities in a secure, invitation-only environment."
        canonical={`https://helvieta.com/${i18n.language}/dancing-with-the-stars`}
        ogImage={dancer}
      />
      <NavBar mode="light" />

      <Box sx={{ backgroundColor: "#050509", color: "#FFFFFF" }}>
        {/* HERO */}
        <Box
          sx={{
            position: "relative",
            height: { xs: "60vh", md: "75vh" },
            overflow: "hidden",
          }}
        >
          {/* HERO IMAGE */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${starimg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "brightness(0.8)",
              zIndex: 0,
            }}
          />

          {/* DARK CINEMATIC GRADIENT (TOP → BOTTOM) */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: `
                linear-gradient(
                  to bottom,
                  rgba(0,0,0,0.9) 0%,
                  rgba(4,4,12,0.75) 25%,
                  rgba(12,8,28,0.55) 50%,
                  rgba(28,18,45,0.35) 70%,
                  rgba(0,0,0,0.15) 100%
                )
              `,
              zIndex: 1,
            }}
          />

          {/* ATMOSPHERIC LIGHT DRIFT */}
          <motion.div
            animate={{
              backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"],
            }}
            transition={{
              duration: 42,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              position: "absolute",
              inset: 0,
              background: `
                radial-gradient(
                  55% 40% at 25% 20%,
                  rgba(110,80,255,0.12),
                  transparent 70%
                ),
                radial-gradient(
                  45% 35% at 75% 80%,
                  rgba(255,120,200,0.08),
                  transparent 75%
                )
              `,
              backgroundSize: "200% 200%",
              zIndex: 2,
              pointerEvents: "none",
            }}
          />

          {/* STARS BURST */}
          {showStars && <StarsBurst />}

          {/* TITLE */}
          <Box
            sx={{
              position: "relative",
              zIndex: 4,
              height: "100%",
              display: "flex",
              alignItems: "flex-end",
              px: { xs: 3, md: 10 },
              pb: { xs: 6, md: 9 },
            }}
          >
            <Typography
              sx={{
                fontFamily: "Playfair Display",
                fontSize: { xs: "2.1rem", md: "3.8rem" },
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                textShadow: "0 8px 30px rgba(0,0,0,0.65)",
              }}
            >
              {t("title")}
            </Typography>
          </Box>
        </Box>

        {/* PRESS */}
        <Box
          sx={{
            py: { xs: 9, md: 12 },
            px: { xs: 3, md: 10 },
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Inter",
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              opacity: 0.6,
              mb: 2,
            }}
          >
            {t("press")}
          </Typography>
          <PressPraiseSection />
        </Box>

        {/* ABOUT */}
        <Box
          sx={{
            py: { xs: 9, md: 13 },
            px: { xs: 3, md: 10 },
          }}
        >
          <DancingWithStarsAbout />
        </Box>

        {/* TESTIMONIALS */}
        <Box
          sx={{
            py: { xs: 10, md: 15 },
            px: { xs: 3, md: 10 },
            borderTop: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Inter",
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              opacity: 0.6,
              mb: 2,
            }}
          >
            {/* {t("testimonials")} */}
          </Typography>
          <DancingWithStarsTestimonials />

          <Box sx={{ height: 160 }} />
        </Box>
      </Box>
    </>
  );
}
